import {
  Control,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { RefObject } from 'react';
import { SelectItemProps, SelectListPosition } from '../select/types';
import { Pagination, Response } from '../../base-types';

export interface Account {
  active: boolean;
  closed: boolean;
  debitBlock: boolean;
  creditBlock: boolean;
  description: string;
  iban: string;
  number: string;
  fullNumber: string;
  clientName: string;
  categoryId: string;
  groupId: string;
  primaryId: string;
  userId?: string;
  organizationId?: string;
  balance?: number;
  closedAt?: string;
  accountType: AccountType;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  totalBalanceBC?: number;
  revaluable: boolean;
  url?: string;
  overdraftEnabled?: boolean;
  externalId?: string;
  closingBalances?: {
    id: string;
    value: number;
    currencyId: string;
    currency: Currency;
  }[];
  render?: Record<string, React.JSX.Element>;
}

export interface Balance {
  accountId: string;
  currencyId: string;
  id: string;
  value: number;
  overdraftEnabled?: boolean;
  overdraftValue?: number;
  balanceWithOverdraft?: number;
}

export interface Currency {
  countries?: Country[];
  active: boolean;
  code: number;
  decimals: number;
  description: string;
  name: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  url?: string;
}

export interface GeneralLedgerTransaction {
  transferDate: string;
  sum: number;
  debitSum?: number;
  creditSum?: number;
  parentTransactionId?: string;
  creditAccountNumber?: string;
  accountId?: string;
  creditAccount?: Account;
  debitAccountNumber?: string;
  debitAccount?: Account;
  creditAccountId?: string;
  debitAccountId?: string;
  documentNumber: string;
  url?: string;
  info: string;
  status: GeneralLedgerStatus;
  currencyId: string;
  currency: Currency;
  transaction?: {
    typeId: string;
    recipientAccount?: { organization?: { type?: CustomerType } };
    senderAccount?: { organization?: { type?: CustomerType } };
  };
  transactionQueueItem?: TransactionQueueItem;
  debitBalanceBefore: number;
  debitBalanceAfter: number;
  creditBalanceBefore: number;
  creditBalanceAfter: number;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  sumBaseCurrency?: number;
  render?: Record<string, React.JSX.Element>;
}

export interface GeneralLedgerTransactionFormValues {
  sum: number;
  debitAccountNumber: string;
  debitAccountId: string;
  creditAccountNumber: string;
  creditAccountId: string;
  parentTransactionId?: string;
  status?: GeneralLedgerStatus;
  documentNumber?: string;
  info: string;
  currencyId: string;
  transferDate?: string;
}

export type InternalTransactionGeneral = Pick<
  BaseTransaction,
  | 'transferDate'
  | 'sum'
  | 'currencyId'
  | 'currency'
  | 'senderAccount'
  | 'recipientAccount'
  | 'senderAccountNumber'
  | 'recipientAccountNumber'
  | 'info'
  | 'documentNumber'
  | 'createdAt'
  | 'contract'
  | 'beneficiary'
  | 'deletedAt'
  | 'depositDateOfDestinationAccount'
>;

export type InternalTransaction = InternalTransactionGeneral;

export type IncomingTransactionGeneral = Pick<
  BaseTransaction,
  | 'currencyId'
  | 'sum'
  | 'transferDate'
  | 'acceptedIn'
  | 'recipientAccount'
  | 'recipientAccountNumber'
  | 'documentNumber'
  | 'reference'
  | 'externalReference'
  | 'paymentType'
  | 'info'
  | 'pspPartnerId'
  | 'pspPartnerAccountNumber'
  | 'currency'
  | 'depositDateOfDestinationAccount'
  | 'createdAt'
  | 'pspIntegrationAccountId'
>;

export type IncomingTransactionOrder = Pick<
  BaseTransaction,
  | 'senderAccount'
  | 'senderAccountNumber'
  | 'remitterFullName'
  | 'remitterCountry'
  | 'remitterAddress'
  | 'remitterBankBIC'
  | 'remitterBankCountry'
  | 'remitterBankName'
  | 'remitterBankAddress'
  | 'intermediaryBankBIC'
  | 'intermediaryBankCountry'
  | 'intermediaryBankCorrespondentAccount'
  | 'intermediaryBankName'
  | 'intermediaryBankAddress'
  | 'remitterBankSortCode'
  | 'remitterCity'
  | 'blockchainTransactionHashId'
  | 'caspId'
  | 'customPassword'
  | 'collectorContact'
  | 'aba'
  | 'externalId'
  | 'baseellaStatus'
>;

export type IncomingTransaction = IncomingTransactionGeneral &
  IncomingTransactionOrder & { deletedAt?: string };

export interface RowProps {
  title: string;
  name: string;
  value?: string | number;
  copyValue?: string | number;
  control?: Control;
  enableEdit?: boolean;
  placeholder?: string;
  children?: React.ReactNode;
  selectList?: SelectItemProps[];
  selectListPosition?: SelectListPosition;
  borderless?: boolean;
  disabled?: boolean;
  error?: string;
  fieldValue?: string;
  setFieldValue?: (data: string) => void;
  setValue?: UseFormSetValue<any>;
  isFixedExchangeAmount?: boolean;
  suffix?: string;
  suffixClassName?: string;
  startDate?: Date;
  tabIndex?: number;
  amount?: string;
  setAmount?: (data?: string) => void;
  currenciesList?: Currency[];
  currencyId?: string;
  exchangeFieldType?: 'rate' | 'margin' | 'inversed-rate';
  isCreate?: boolean;
  copyEnabled?: boolean;
  className?: string;
  exchangeInverseRate?: number;
  exchangeInverseBaseRate?: number;
  exchangeRate?: number;
  baseRate?: number;
  exchangeType?: string;
  accountType?: string;
  withoutFormatting?: boolean;
  withTime?: boolean;
  hasExternalAccountValue?: boolean;
  calculateTransactionRatesByIdAction?: (
    calculateFields: {
      exchangeDateOfRate?: string;
      exchangeRateSourceId?: string;
      exchangeMarginPercent?: number;
      exchangeActualRate?: number;
      inverseActualRate?: number;
    },
    type: 'rate' | 'margin' | 'date' | 'inversed-rate',
  ) => void;
  withHint?: boolean;
  hintText?: string;
  rightHint?: boolean;
  smallHintWidth?: boolean;
  maxLength?: number;
  withShortcut?: boolean;
  getFieldValue?: (data: string) => void;
  onFocus?: () => void;
  withClearButton?: boolean;
  clearErrors?: UseFormClearErrors<any>;
  toleranceRow?: boolean;
  setError?: UseFormSetError<any>;
}

export interface AutocompleteRowProps {
  title: string;
  name: string;
  value?: string;
  control?: Control;
  enableEdit?: boolean;
  placeholder?: string;
  children?: React.ReactNode;
  autocompleteList?: SelectItemProps[];
  isLoading?: boolean;
  disabled?: boolean;
  error?: string;
  withSuffix?: boolean;
  onSearchAutocomplete?: (name: string, search: string) => void;
  setValue?: UseFormSetValue<any>;
  isFixedExchangeCurrency?: boolean;
  tabIndex?: number;
  withClearButton?: boolean;
  swap?: boolean;
  copyValue?: string;
  copyEnabled?: boolean;
  className?: string;
  hasExternalAccountValue?: boolean;
  pagination?: Pagination;
  onScroll?: () => void;
  nextPageIsLoading?: boolean;
  withInfiniteScroll?: boolean;
  clearErrors?: UseFormClearErrors<any>;
}

export type OutgoingTransaction = OutgoingTransactionGeneral &
  OutgoingTransactionOrder & {
    deletedAt?: string;
    iban?: string;
    bic?: string;
    disableExternalTransactionCreate?: boolean;
  };

export type OutgoingTransactionGeneral = Pick<
  BaseTransaction,
  | 'currencyId'
  | 'sum'
  | 'transferDate'
  | 'acceptedOut'
  | 'senderAccount'
  | 'senderAccountNumber'
  | 'documentNumber'
  | 'paymentType'
  | 'chargeType'
  | 'info'
  | 'pspPartnerId'
  | 'pspPartnerAccountNumber'
  | 'currency'
  | 'createdAt'
  | 'refundAmount'
  | 'refundFees'
  | 'refundedAt'
  | 'pspIntegrationAccountId'
>;

export type OutgoingTransactionOrder = Pick<
  BaseTransaction,
  | 'recipientAccount'
  | 'recipientAccountNumber'
  | 'benificiaryBankSortCode'
  | 'benificiaryFullName'
  | 'benificiaryCountry'
  | 'benificiaryAddress'
  | 'benificiaryType'
  | 'benificiaryBIC'
  | 'benificiaryBankName'
  | 'benificiaryBankCountry'
  | 'benificiaryBankAddress'
  | 'intermediaryBankBIC'
  | 'intermediaryBankCountry'
  | 'intermediaryBankCorrespondentAccount'
  | 'intermediaryBankName'
  | 'intermediaryBankAddress'
  | 'benificiaryCity'
  | 'contract'
  | 'beneficiary'
  | 'blockchainTransactionHashId'
  | 'caspId'
  | 'collectorContact'
  | 'customPassword'
  | 'aba'
  | 'externalId'
  | 'purposeCode'
  | 'benificiaryPostCode'
>;

export enum ExchangeTypeEnum {
  SELL = 'SELL',
  BUY = 'BUY',
}

export interface TransactionAML {
  senderRiskScore: number;
  recipientRiskScore: number;
  productScore: number;
  countryScore: number;
  transactionScore: number;
  totalRiskScore: number;
  counterpartyCountryScore: number;
  counterpartyBankCountryScore: number;
  singleTransactionVolumeScore: number;
  multipleTransactionVolumeScore: number;
  comment: string;
}

export enum EnumTabs {
  GENERAL = 'GENERAL',
  ORDER = 'ORDER',
  WARNING = 'WARNING',
  AML = 'AML',
  LOG = 'LOG',
  CHANGE_LOG = 'CHANGE_LOG',
  SIGNATURES = 'SIGNATURES',
  DOCUMENTS = 'DOCUMENTS',
  GL_TRANSACTIONS = 'GL_TRANSACTIONS',
  CHATS = 'CHATS',
  COMMENTS = 'COMMENTS',
  INTEGRATION_LOGS = 'INTEGRATION_LOGS',
}

export enum InternalEnumTabs {
  GENERAL = 'GENERAL',
  WARNING = 'WARNING',
  AML = 'AML',
  LOG = 'LOG',
  CHANGE_LOG = 'CHANGE_LOG',
  SIGNATURES = 'SIGNATURES',
  DOCUMENTS = 'DOCUMENTS',
  GL_TRANSACTIONS = 'GL_TRANSACTIONS',
  CHATS = 'CHATS',
  COMMENTS = 'COMMENTS',
}

export enum EnumNostroTabs {
  GENERAL = 'GENERAL',
  LOG = 'LOG',
  CHANGE_LOG = 'CHANGE_LOG',
  SIGNATURES = 'SIGNATURES',
  GL_TRANSACTIONS = 'GL_TRANSACTIONS',
  COMMENTS = 'COMMENTS',
  INTEGRATION_LOGS = 'INTEGRATION_LOGS',
}

export enum EnumExchangeTabs {
  GENERAL = 'GENERAL',
  LOG = 'LOG',
  CHANGE_LOG = 'CHANGE_LOG',
  SIGNATURES = 'SIGNATURES',
  DOCUMENTS = 'DOCUMENTS',
  GL_TRANSACTIONS = 'GL_TRANSACTIONS',
  CHATS = 'CHATS',
  COMMENTS = 'COMMENTS',
  SYNCED_TRANSACTIONS = 'SYNCED_TRANSACTIONS',
  INTEGRATION_LOGS = 'INTEGRATION_LOGS',
}

export type ExchangeTransaction = Pick<
  BaseTransaction,
  | 'baseellaStatus'
  | 'exchangeProfit'
  | 'exchangeDateOfRate'
  | 'exchangeRateSourceId'
  | 'exchangeCurrencyFromId'
  | 'exchangeCurrencyToId'
  | 'exchangeCurrencyFixedId'
  | 'exchangeMarginPercent'
  | 'exchangeActualRate'
  | 'transferDate'
  | 'sum'
  | 'senderAccountNumber'
  | 'recipientAccountNumber'
  | 'senderAccount'
  | 'recipientAccount'
  | 'exchangeCurrencyFlexibleSum'
  | 'exchangeRate'
  | 'exchangeCurrencyFrom'
  | 'exchangeCurrencyTo'
  | 'exchangeRateSource'
  | 'depositDateOfDestinationAccount'
  | 'blockchainTransactionHashId'
> & {
  exchangeType?: string;
  calculatedAmount?: string;
  baseRateToCurrencyFrom?: number;
  baseRateToCurrencyTo?: number;
  baseRate?: number;
  exchangeProfit?: number;
  providersRate?: number;
  exchangeBaseRate?: number;
  createdAt?: string;
  deletedAt?: string;
  sellSum?: string;
  buySum?: string;
  exchangeInverseRate?: number;
  exchangeInverseBaseRate?: number;
  exchangeInverseActualRate?: number;
  exchangeRateTolerance?: number | null;
  disableExternalTransactionCreate?: boolean;
};

export type NostroTransaction = Pick<
  BaseTransaction,
  | 'transferDate'
  | 'acceptedIn'
  | 'acceptedOut'
  | 'sum'
  | 'currencyId'
  | 'paymentType'
  | 'senderAccount'
  | 'recipientAccount'
  | 'senderAccountNumber'
  | 'recipientAccountNumber'
  | 'documentNumber'
  | 'info'
  | 'createdAt'
  | 'caspId'
  | 'purposeCode'
> & {
  toPartnerPspId?: string;
  fromPartnerPspId?: string;
  currency?: { name: string };
  deletedAt?: string;
  disableExternalTransactionCreate?: boolean;
};

export interface TransactionType {
  name: string;
  active: string;
  id: string;
  children?: TransactionSubtype[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface TransactionSubtype {
  name: string;
  active: string;
  id: string;
  parents?: TransactionType[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface Country {
  name: string;
  riskScore: string;
  numeric: string;
  alpha2: string;
  alpha3: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  url?: string;
}

export interface ClientUserEntity {
  url?: string;
  organizations?: Response<string[]>;
  userStatus: string;
  userType: string;
  employmentStatus: string;
  coffStatus: string;
  countryOfResidenceId: string;
  countryOfResidence?: Country;
  name: string;
  dateOfBirth: string;
  nationality: string;
  phone: string;
  email: string;
  residentialAddress: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

export interface BaseTransaction {
  feeAmount?: number;
  feeAmountBaseCurrency?: number;
  customPassword?: string;
  consumerStatus: СonsumerStatus;
  baseellaStatus: BaseellaStatus;
  type: TransactionType;
  subtype?: TransactionSubtype;
  senderUser?: ClientUserEntity;
  recipientUser?: ClientUserEntity;
  benificiaryType?: BenificiaryType;
  transferDate?: string;
  acceptedIn?: string;
  acceptedOut?: string;
  sum: number;
  sumBaseCurrency?: number;
  email?: string;
  documentNumber: string;
  info: string;
  typeId: string;
  senderUserId?: string;
  recipientUserId?: string;
  remitterName?: string;
  collectorContact?: string;
  remitterFullName?: string;
  remitterCity?: string;
  remitterAddress?: string;
  remitterCountry?: string;
  createdByOrganizationId?: string;
  remitterAccountNumber?: string;
  remitterPaymentReferenceNumber?: string;
  remitterBankName?: string;
  remitterBankAddress?: string;
  remitterBankBIC?: string;
  remitterBankSortCode?: string;
  remitterBankSwiftCode?: string;
  remitterBankCountry?: string;
  remitterBankCorrespondentAccount?: string;
  benificiaryBankSortCode?: string;
  benificiaryBankSwiftCode?: string;
  benificiaryBIC?: string;
  benificiaryBankCountry?: string;
  benificiaryBankCorrespondentAccount?: string;
  benificiaryBankName?: string;
  benificiaryBankAddress?: string;
  benificiaryFullName?: string;
  benificiaryCity?: string;
  benificiaryCountry?: string;
  benificiaryAddress?: string;
  intermediaryBankName?: string;
  intermediaryBankCountry?: string;
  intermediaryBankAddress?: string;
  intermediaryBankBIC?: string;
  intermediaryBankCorrespondentAccount?: string;
  intermediaryBankSwiftCode?: string;
  sepaBeneficiaryAccount?: string;
  sepaBeneficiaryName?: string;
  sepaBeneficiaryCountry?: string;
  sepaBeneficiaryAddress?: string;
  senderAccount?: any;
  senderAccountNumber: string;
  recipientAccount?: any;
  recipientAccountNumber: string;
  currencyId: string;
  currency?: Currency;
  balanceBefore?: number;
  balanceAfter?: number;
  senderBalanceBefore?: number;
  senderBalanceAfter?: number;
  recipientBalanceBefore?: number;
  recipientBalanceAfter?: number;
  correspondentAccount?: string;
  reference?: string;
  externalReference?: string;
  externalId?: string;
  chargeType: ChargeType;
  exportBatchId?: string;
  monitoringStatus: MonitoringStatus;
  aba?: string;
  riskScore?: number;
  transactionQueueItem?: TransactionQueueItem;
  pspPartnerId?: string;
  pspPartnerAccountNumber?: string;
  depositDateOfDestinationAccount?: string;
  blockchainTransactionHashId?: string;
  caspId?: string;
  latestQueuedStatuses?: {
    baseellaStatus?: BaseellaStatus;
    consumerStatus?: BaseellaStatus;
  };
  contract?: {
    beneficiaryId: string;
    createdAt: string;
    deletedAt: string;
    description: string;
    id: string;
    name: string;
    status: string;
    sum: number;
    updatedAt: string;
    currencyId: string;
  };
  beneficiary?: {
    id: string;
    name: string;
    city?: string;
    beneficiaryType?: string;
    collectorContact?: string;
    organization: { type: string; id: string };
  };
  url?: string;
  id: string;
  paymentType?: PaymentType;
  logs?: {
    edges: {
      node: {
        createdAt: string;
        diff: {
          prev: Record<string, string | number>;
          next: Record<string, string | number>;
        };
        employeeId?: string;
        employeeName?: string;
        id: string;
        transactionId: string;
      };
    }[];
  };
  generalLedgerTransactions?: {
    edges: { cursor: string; node: GeneralLedgerTransaction }[];
    pageInfo?: Pagination;
  };
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  exchangeCurrencyFromId?: string;
  exchangeCurrencyToId?: string;
  exchangeRateSourceId?: string;
  exchangeDateOfRate?: string;
  exchangeRate?: number;
  exchangeActualRate?: number;
  exchangeCurrencyFixedId?: string;
  exchangeCurrencyFixedSum?: string;
  exchangeCurrencyFixed?: Currency;
  exchangeCurrencyFrom?: Currency;
  exchangeCurrencyTo?: Currency;
  exchangeMarginPercent?: number;
  exchangeProfit?: number;
  exchangeCurrencyFlexibleSum?: number;
  exchangeRateSource?: { description: string; id: string; name: string };
  pspIntegrationAccountId?: string;
  pspIntegrationAccount?: ExternalAccount;
  refundAmount?: number;
  refundFees: boolean;
  refundedAt?: string;
  values?: any[];
  marginCurrency?: string;
  exchangeType?: string;
  purposeCode?: string;
  benificiaryPostCode?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface TransactionQueueItem {
  transaction?: BaseTransaction;
  transactionId: string;
  generalLedgerTransaction?: GeneralLedgerTransaction;
  generalLedgerTransactionId?: string;
  affectedAccounts: string[];
  status: MonitoringStatus;
  action?: TransactionQueueItemAction;
}

export enum СonsumerStatus {
  DRAFT = 'DRAFT',
  NONE = 'NONE',
  NEW = 'NEW',
  SENT = 'SENT',
  DEBITED = 'DEBITED',
  CREDITED = 'CREDITED',
  PROVIDE_INFO = 'PROVIDE_INFO',
  REJECTED = 'REJECTED',
}

export enum BaseellaStatus {
  DRAFT = 'DRAFT',
  NONE = 'NONE',
  NEW = 'NEW',
  PROC = 'PROC',
  SENT = 'SENT',
  ARCH = 'ARCH',
  REV = 'REV',
  AML = 'AML',
  AML_INFO = 'AML_INFO',
  AML_REJ = 'AML_REJ',
  AML_DONE = 'AML_DONE',
  REJECTED = 'REJECTED',
  NOT_SIGNED = 'NOT_SIGNED',
  READY_TO_SEND = 'READY_TO_SEND',
  READY_TO_PSP = 'READY_TO_PSP',
  ACCP_OUT = 'ACCP_OUT',
  UNDEFINED_FUNDS = 'UNDEFINED_FUNDS',
  FX_RATE_REQUEST = 'FX_RATE_REQUEST',
  REFUNDED = 'REFUNDED',
  BOOKED = 'BOOKED',
  HELD = 'HELD',
  MISSING_PAYER = 'MISSING_PAYER',
}

export enum BaseellaStatusTitle {
  DRAFT = 'Draft',
  NONE = 'None',
  NEW = 'New',
  PROC = 'Proc',
  SENT = 'Sent',
  ARCH = 'ARCH',
  REV = 'Rev',
  AML = 'AML',
  AML_INFO = 'AML Info',
  AML_REJ = 'AML Rejected',
  AML_DONE = 'AML Done',
  REJECTED = 'Rejected',
  NOT_SIGNED = 'Not Signed',
  READY_TO_SEND = 'Ready to Send',
  READY_TO_PSP = 'Ready to PSP',
  ACCP_OUT = 'ACCP Out',
  UNDEFINED_FUNDS = 'Undefined Funds',
  FX_RATE_REQUEST = 'Rate Request',
  REFUNDED = 'Refunded',
  BOOKED = 'Booked',
  HELD = 'Held',
  MISSING_PAYER = 'Missing Payer',
}

export enum LogStatusEnum {
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export interface TransactionStatusLog {
  createdAt: string;
  creatorComment?: string;
  creatorName?: string;
  creatorUserId?: string;
  id: string;
  status: LogStatusEnum;
  creatorUser?: { profile?: { firstName: string; lastName: string } };
  transactionId: string;
  txBaseellaStatusFrom?: BaseellaStatus;
  txBaseellaStatusTo?: BaseellaStatus;
  updatedAt?: string;
}

export interface BaseellaStatusTransition {
  allowed: boolean;
  baseellaStatusNext: BaseellaStatus;
  errors?: string[];
  warnings?: string[];
}

export enum MonitoringStatus {
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

export enum ChargeType {
  BEN = 'BEN',
  SHA = 'SHA',
  OUR = 'OUR',
}

export enum GeneralLedgerStatus {
  PENDING = 'PENDING',
  ARCH = 'ARCH',
}

export enum TransactionQueueItemAction {
  REMOVE_TRANSACTION = 'REMOVE_TRANSACTION',
}

export enum AccountType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
}

export enum BenificiaryType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export enum PaymentType {
  FASTER_PAYMENT = 'FASTER_PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA_SCT_INST',
  SEPA_SCT = 'SEPA_SCT',
  TARGET_2 = 'TARGET_2',
  SWIFT = 'SWIFT',
  CRYPTO_ASSET_PAYMENT = 'CRYPTO_ASSET_PAYMENT',
  CASH_OVER_COUNTER = 'CASH_OVER_COUNTER',
  FEDWIRE = 'FEDWIRE',
  UAE_FTS = 'UAE_FTS',
  UAE_IPP = 'UAE_IPP',
}

export enum PaymentTypeTitle {
  FASTER_PAYMENT = 'FASTER PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA SCT INST',
  SEPA_SCT = 'SEPA SCT',
  TARGET_2 = 'TARGET 2',
  SWIFT = 'SWIFT',
  CRYPTO_ASSET_PAYMENT = 'Crypto Asset Payment',
  CASH_OVER_COUNTER = 'Cash Over Counter',
  FEDWIRE = 'Fedwire',
  UAE_FTS = 'UAE FTS',
  UAE_IPP = 'UAE Ipp',
}

export enum TransactionTypeEnum {
  INTERNAL_PAYMENT = 'INTERNAL_PAYMENT',
  CURRENCY_EXCHANGE = 'CURRENCY_EXCHANGE',
  NOSTRO = 'NOSTRO',
  GENERAL_LEDGER = 'GENERAL_LEDGER',
  INTERNAL = 'INTERNAL',
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export enum TransactionWarningTypeEnum {
  BLACKLIST = 'BLACKLIST',
  SCORING = 'SCORING',
  MONITORING = 'MONITORING',
  AGREEMENT = 'AGREEMENT',
  AML_BOT = 'AML_BOT',
}

export enum TransactionWarningActionEnum {
  STOP = 'STOP',
  PASS_ONCE = 'PASS_ONCE',
  PASS_COMBINATION = 'PASS_COMBINATION',
  PASS_ALL_TIME_FOR_THIS_CLIENT = 'PASS_ALL_TIME_FOR_THIS_CLIENT',
  RFI_TO_CLIENT_DOCS = 'RFI_TO_CLIENT_DOCS',
  RFI_TO_CLIENT_CLARIFICATIONS = 'RFI_TO_CLIENT_CLARIFICATIONS',
}

export enum CounterPartyEnum {
  ANY = 'ANY',
  SAME = 'SAME',
}

export enum ClientType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
}

export enum MonitoringRuleAction {
  NONE = 'NONE',
  OPS_REVIEW = 'OPS_REVIEW',
  AML_REVIEW = 'AML_REVIEW',
}

export enum GenderEnum {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export interface EntityAddress {
  id: string;
  address1: string;
  address2: string;
  addressTypeDesc: string;
  city: string;
  country: string;
  entityAddressGUID: string;
  isoStandard: string;
  lastUpdated: string;
  postalCode: string;
  stateProvinceRegion: string;
}

export interface EntityAliases {
  aliasTypeDesc: string;
  englishDescription: string;
  entityAliasGUID: string;
  firstName: string;
  lastName: string;
  lastUpdated: string;
  middleName: string;
  name: string;
  prefix: string;
  suffix: string;
  id: string;
}

export interface EntityCountryAssociations {
  id: string;
  administrativeUnitName: string;
  associationTypeDesc: string;
  entityCountryAssociationGUID: string;
  isoStandard: string;
  lastUpdated: string;
  ownershipPercentageCalc: string | number;
}

export interface EntityIdentifications {
  id: string;
  administrativeUnitName: string;
  entityIdentificationGUID: string;
  expirationDate?: {
    expirationDay: string;
    expirationMonth: string;
    expirationYear: string;
  };
  expirationDateValue?: string;
  identificationIssuer: string;
  identificationNumber: string;
  identificationTypeDesc: string;
  isoStandard: string;
  issueDate?: { issueDay: string; issueMonth: string; issueYear: string };
  issueDateValue?: string;
  lastUpdated?: string;
}

export interface EntityPositions {
  id: string;
  entityPositionGUID: string;
  lastUpdated: string;
  position: string;
}

export interface EntityRelationships {
  id: string;
  entityRelationshipGUID: string;
  groupDesc: string;
  lastUpdated: string;
  ownershipPercentage: string;
  relatedEntityGUID: string;
  relationshipDesc: string;
  render?: Record<string, React.JSX.Element>;
}

export interface EntityRemarks {
  entityRemarkGUID: string;
  lastUpdated: string;
  remark: string;
}

export interface EntitySourceItems {
  entitySourceItemGUID: string;
  lastUpdated: string;
  sourceURI: string;
}

export interface EntityAdverseMedias {
  adverseMediaDesc: string;
  entityAdverseMediaGUID: string;
  entityAdverseMediaSubcategories: {
    entityAdverseMediaSubCategoryGUID: string;
    lastUpdated: string;
    subCategoryLabel: string;
  }[];
  subCategories?: string;
  lastUpdated: string;
}

export interface EntityEnforcements {
  administrativeUnitName: string;
  enforcementDesc: string;
  entityEnforcementGUID: string;
  entityEnforcementSubcategories: {
    entityEnforcementSubCategoryGUID: string;
    lastUpdated: string;
    subCategoryLabel: string;
  }[];
  subCategories?: string;
  isoStandard: string;
  lastUpdated: string;
  sourceName: string;
  sourceNameAbbrev: string;
}

export interface EntityPEP {
  id: string;
  administrativeUnitLevel0: string;
  administrativeUnitLevel1: string;
  administrativeUnitLevel2: string;
  administrativeUnitLevel3: string;
  administrativeUnitLevel4: string;
  effectiveDate: {
    effectiveDay: string;
    effectiveMonth: string;
    effectiveYear: string;
  };
  effectiveDateValue?: string;
  effectiveDateTypeDesc: string;
  entityPEPGUID: string;
  entityPEPSubcategories: {
    entityPEPSubCategoryGUID: string;
    lastUpdated: string;
    subCategoryDesc: string;
    subCategoryLabel: string;
  }[];
  expirationDate: {
    expirationDay: string;
    expirationMonth: string;
    expirationYear: string;
  };
  expirationDateValue?: string;
  expirationDateTypeDesc: string;
  governingInstitution: string;
  governingRole: string;
  isActivePEP: string;
  isInCountryPEPOnly: string;
  isPrimaryPEP: string;
  isoAdministrativeUnitLevel0: string;
  lastUpdated: string;
  pepAdminLevelDesc: string;
  flags?: string;
}

export interface EntitySanctions {
  id: string;
  administrativeUnitName: string;
  consolidatedSanction?: {
    consolidatedSanctionGUID: string;
    entityGUID: string;
    lastUpdated: string;
  };
  consolidated?: string;
  entitySanctionDesignations?: {
    entitySanctionDesignationGUID: string;
    designationName: string;
    subDesignationName: string;
    entitySanctionGUID: string;
    lastUpdated: string;
  }[];
  designations?: string;
  entitySanctionGUID: string;
  isoStandard: string;
  lastUpdated: string;
  sourceName: string;
  sourceNameAbbrev: string;
}

export interface EntitySOE {
  id: string;
  entitySOEDomains: {
    entitySOEDomainGUID: string;
    lastUpdated: string;
    soeDomainDesc: string;
  }[];
  domains?: string;
  entitySOEGUID: string;
  entitySOESubcategories?: {
    entitySOESubCategoryGUID: string;
    lastUpdated: string;
    subCategoryLabel: string;
  }[];
  subCategories?: string;
  isActive: string;
  lastUpdated: string;
}

export interface AdditionalSegmentField {
  id: string;
  derivedName: string;
  derivedValue: string;
}

export interface AdditionalSegment {
  lastUpdated: string;
  segmentGUID: string;
  type: string;
  additionalSegmentFields: AdditionalSegmentField[];
}

export interface BlacklistData {
  gender: GenderEnum;
  blacklist?: { source?: string };
  blacklistId: string;
  fullName: string;
  aliases: string[];
  nameNonLatin: string;
  dateOfBirth?: string[];
  nationality: string;
  address: string[];
  countryOfResidence: string[];
  countryName: string;
  countryCodes: string[];
  personalIdNumber: string[];
  placeOfBirth: string[];
  additionalInfo: string;
  reason: string;
  bic: string;
  swift: string[];
  pspAddress: string;
  pspName: string;
  id: string;
  createdAt: string;
  url?: string;
}

export enum AMLBotType {
  ADDRESS = 'ADDRESS',
  TRANSACTION = 'TRANSACTION',
}

export interface BlacklistUserInfoData {
  id: string;
  primaryTerm?: any;
  score?: number;
  seqNo?: any;
  sort?: string;
  name?: string;
  createdAt?: string;
  lastUpdated?: string;
  source: {
    createdAt?: string;
    lastUpdated?: string;
    firstName: string;
    lastName: string;
    gender?: string;
    blacklistId: string;
    entityTypeDesc?: string;
    name: string;
    entityGUID?: string;
    entityID?: string;
    lookupID?: string;
    isDeceased?: string;
    entityAddresses?: EntityAddress[];
    entityAliases?: EntityAliases[];
    entityCountryAssociations: EntityCountryAssociations[];
    entityDOBs?: {
      birthDate: { birthDay: string; birthMonth: string; birthYear: string };
      entityDOBGUID: string;
      lastUpdated: string;
    }[];
    entityIdentifications?: EntityIdentifications[];
    entityPositions?: EntityPositions[];
    entityRelationships?: EntityRelationships[];
    entityRemarks?: EntityRemarks[];
    entitySourceItems?: EntitySourceItems[];
    entitySegments?: {
      entityAdverseMedias?: EntityAdverseMedias[];
      entityEnforcements?: EntityEnforcements[];
      entityPEPs?: EntityPEP[];
      entitySanctions?: EntitySanctions[];
      entitySOEs?: EntitySOE[];
      additionalSegments?: AdditionalSegment[];
    };
  };
  url: string;
  render?: Record<string, React.JSX.Element>;
}

export interface TransactionWarning {
  url?: string;
  type: TransactionWarningTypeEnum;
  transactionId: string;
  blacklistId?: string;
  applicationId?: string;
  application?: {
    id: string;
    type: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
  };
  monitoringRuleId?: string;
  scoreResultId?: string;
  action?: TransactionWarningActionEnum;
  actions?: TransactionWarningActionEnum;
  organizationId?: string;
  organization?: {
    id: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    type?: string;
  };
  employeeId?: string;
  employee?: { id: string; profile: { firstName: string; lastName: string } };
  blacklistMatchScore?: number;
  blacklistMatchResult?: string;
  blacklistMatchingPart?: string;
  blacklistDoc?: BlacklistData;
  blacklistItem?: BlacklistUserInfoData;
  comment?: string;
  createdAt: string;
  riskScore?: number;
  amlType?: AMLBotType;
  matchStatus?: string;
  amlBotReport?: string;
  paymentOrderBeneficiary?: {
    id: string;
    senderName?: string;
    localAmount: number;
    localCurrency: Currency;
    destAmount: number;
    destCurrency: Currency;
    createdAt: string;
    paymentOrderId?: string;
    paymentOrder?: {
      recepientAccount?: {
        organization: {
          id: string;
          companyName?: string;
          type?: string;
          firstName?: string;
          lastName?: string;
        };
      };
    };
  };
  monitoringRule?: {
    transactionTypeId?: string;
    paymentType?: PaymentType;
    name?: string;
    number?: number;
    volumeFrom?: number;
    volumeTo?: number;
    periodOfTimeInHours?: number;
    counterParty?: CounterPartyEnum;
    clientType?: ClientType;
    active: boolean;
    action?: MonitoringRuleAction;
    id: string;
  };
  warningsToPaymentOrderBeneficiaries?: {
    edges?: {
      node: {
        id: string;
        paymentOrderBeneficiary: {
          id: string;
          senderName?: string;
          localAmount: number;
          localCurrency: Currency;
          destAmount: number;
          destCurrency: Currency;
          createdAt: string;
          paymentOrderId?: string;
          paymentOrder?: {
            recepientAccount?: {
              organization: {
                id: string;
                companyName?: string;
                type?: string;
                firstName?: string;
                lastName?: string;
              };
            };
          };
        };
      };
    }[];
    pageInfo: Pagination;
  };
  scoreResult?: {
    transactionId: string;
    productScore?: number;
    senderRiskScore?: number;
    recipientRiskScore?: number;
    countryScore?: number;
    transactionScore?: number;
    totalRiskScore?: number;
    action?: TransactionWarningActionEnum;
    comment?: string;
    counterpartyCountryScore?: number;
    counterpartyBankCountryScore?: number;
    singleTransactionVolumeScore?: number;
    multipleTransactionVolumeScore?: number;
    additionalInfo?: string;
    id: string;
  };
  blacklist?: {
    name: string;
    active: boolean;
    description?: string;
    successfulSyncLastDate?: string;
    lastTrySyncDate?: string;
    nextTrySyncDate?: string;
    source?: string;
    id: string;
  };
  relatedTransactions?: {
    id: string;
    transferDate: string;
    sum: number;
    currency: { name: string };
    typeId: string;
  }[];
  id: string;
  updatedAt?: string;
  warningGroupId?: string;
  warningGroup?: {
    applicationId?: string;
    createdAt?: string;
    deletedAt?: string;
    id: string;
    organizationId?: string;
    organizationType?: string;
    paymentOrderId?: string;
    paymentOrderType?: string;
    transactionId?: string;
    transactionPaymentType?: string;
    transactionTypeId?: string;
    updatedAt?: string;
  };
}

export interface TransactionWarningActionBody {
  action: string;
  comment: string;
  employeeId: string;
  organizationId?: string;
  matchStatus?: string;
}

export interface AccountBalance {
  id: string;
  accountId: string;
  currencyId: string;
  value: number;
}

export enum SignatureType {
  TRANSACTION = 'TRANSACTION',
  DOCUMENT = 'DOCUMENT',
}

export interface Signature {
  type: SignatureType;
  mfaToken: string;
  signedAt: string;
  checksum?: string;
  organizationId?: string;
  userId?: string;
  transactionId?: string;
  documentId?: string;
  id?: string;
  user?: {
    profile: { firstName: string; lastName: string };
    serviceRole: string[];
  };
  createdAt?: string;
  updatedAt?: string;
  valid?: boolean;
}

export enum CustomerType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}

export enum ChannelType {
  MAIN = 'MAIN',
  NOTIFICATION = 'NOTIFICATION',
  APPLICATION = 'APPLICATION',
  TRANSACTION = 'TRANSACTION',
  DEMO = 'DEMO',
}

export enum ChannelStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export interface Channel {
  applicationId?: string;
  application?: {
    companyName?: string;
    firstName?: string;
    lastName?: string;
    type?: CustomerType;
  };
  messages: {
    edges: {
      node: {
        content: string;
        channelId: string;
        createdAt: string;
        document?: { name: string };
      };
    }[];
  };
  channelType: ChannelType;
  closureType: string;
  createdAt: string;
  deletedAt?: string;
  employee?: {
    profile?: { firstName?: string; lastName?: string };
    email?: string;
  };
  employeeId?: string;
  id: string;
  organization?: {
    companyName?: string;
    firstName?: string;
    lastName?: string;
    type?: CustomerType;
  };
  user?: {
    phone?: string;
    email?: string;
    profile?: { dob?: string; firstName?: string; lastName?: string };
  };
  userId?: string;
  organizationId?: string;
  status: ChannelStatus;
  title: string;
  transactionId?: string;
  typeId?: TransactionTypeEnum;
  updatedAt?: string;
}

export interface CofProps {
  userId: string;
  organizationId: string;
  status: string;
  position: string;
  companyOfficialType: string;
  id: string;
  user: {
    status: string;
    totpEnabled?: boolean;
    serviceRole: string[];
    organization?: { id?: string };
    profile: {
      firstName: string;
      lastName: string;
      dob: string;
      id: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: string;
    };
  };
}

export interface Employee {
  id: string;
  profile: { firstName: string; lastName: string };
  email?: string;
  status: string;
}

export enum DocumentStatus {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export enum DocumentType {
  APPLICATION_INCORPORATION_CERTIFICATE = 'APPLICATION_INCORPORATION_CERTIFICATE',
  APPLICATION_ARTICLES_OF_ASSOCIATIONS = 'APPLICATION_ARTICLES_OF_ASSOCIATIONS',
  APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS = 'APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS',
  APPLICATION_REGISTRY_OF_THE_DIRECTORS = 'APPLICATION_REGISTRY_OF_THE_DIRECTORS',
  APPLICATION_ANNUAL_ACCOUNTS = 'APPLICATION_ANNUAL_ACCOUNTS',
  CLIENT_IDENTIFICATION = 'CLIENT_IDENTIFICATION',
  CLIENT_TRANSACTION = 'CLIENT_TRANSACTION',
  CLIENT_PSP_CUSTOMER = 'CLIENT_PSP_CUSTOMER',
  CLIENT_CDD_CHECK = 'CLIENT_CDD_CHECK',
  OTHER = 'OTHER',
}

export interface DocumentComment {
  createdAt: string;
  deletedAt: string;
  documentId: string;
  id: string;
  text: string;
  updatedAt: string;
  userId: string;
  user: { profile: { firstName: string; lastName: string } };
}

export interface Document {
  name: string;
  uploadId: string;
  category: DocumentType;
  expirationDate: string;
  status: DocumentStatus;
  userId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  isSelected?: boolean;
  commentsList?: DocumentComment[];
  organizationId?: string;
  upload?: { mimeType?: string };
  countComments?: { count: number };
  comments?: { edges: { node: DocumentComment }[] };
  warning?: boolean;
  warningMessage?: string;
}

export interface CASP {
  createdAt: string;
  id: string;
  name: string;
  currencies: {
    active: boolean;
    caspId: string;
    createdAt: string;
    currencyId: string;
    currency: Currency;
    id: string;
  }[];
}

export enum ExportFileTypeEnum {
  HARBOUR_HILLS_B2B_LITE = 'HARBOUR_HILLS_B2B_LITE',
  SPITAMEN = 'SPITAMEN',
  NULL = 'NULL',
}

export enum ConnectionTypeEnum {
  CSM = 'CSM',
  VIRTUAL_IBAN = 'VIRTUAL_IBAN',
  NO_IBAN = 'NO_IBAN',
}

export enum CurrencyType {
  ALL = 'ALL',
  NONE = 'NONE',
  SPECIFIC = 'SPECIFIC',
}

export enum PspPartnerTypeEnum {
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  EMONEY = 'EMONEY',
}

export enum PspPartnerLocationEnum {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

export interface ConnectionType {
  pspIntegrationPaymentId: string;
  connectionType: ConnectionTypeEnum;
  id: string;
}

export interface PspIntergrationCurrency {
  pspIntegrationPaymentId: string;
  currencyId: string;
  currency: Currency;
  id: string;
}

export interface PspPartner {
  partnerType: PspPartnerTypeEnum;
  location: PspPartnerLocationEnum;
  paymentsTypes: string[];
  companyName: string;
  legalName: string;
  swift?: string;
  sortCode?: string;
  pspIntegrationId?: string;
  pspIntegration?: {
    name: string;
    pspIntegrationPayments: PspIntegrationPayment[];
    id: string;
  };
  address: string;
  managerContacts?: string;
  onlineBankLink?: string;
  description?: string;
  active: boolean;
  visibleToClients: boolean;
  countryId: string;
  country: Country;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  beneficiary?: string;
  exportFileType?: ExportFileTypeEnum;
  id: string;
}

export interface PaymentsPartnersSetting {
  pspIntegrationPaymentId: string;
  pspIntegrationPayment: PspIntegrationPayment;
  pspPartnerId: string;
  pspPartner: PspPartner;
  bic?: string;
  sortCode?: string;
  id: string;
}

export interface PspIntegrationPayment {
  pspIntegrationId: string;
  paymentsTypes: string[];
  currencyType: CurrencyType;
  connectionsTypes: ConnectionType[];
  currencies: PspIntergrationCurrency[];
  paymentsPartnersSettings: PaymentsPartnersSetting[];
  id: string;
}

export interface ExternalAccount {
  account: Account & { organization: { type: string } };
  accountId?: string;
  iban: string;
  id: string;
  pspIntegrationConnectionId: string;
  pspIntegrationPayment?: PspIntegrationPayment;
  pspIntegrationPaymentId?: string;
  pspPartner?: PspPartner;
  bank?: string;
  currencyId?: string;
  paymentType?: string;
  accountNumber?: string;
  accountName?: string;
  accountType?: string;
  active?: boolean;
  createdAt?: string;
  createType?: string;
  render?: Record<string, React.JSX.Element>;
}

export enum MatchStatusEnum {
  TRUE = 'TRUE',
  FALSE = 'FALSE',
  POTENTIAL = 'POTENTIAL',
}

export interface TransactionAccountRowReadTypes {
  title: string;
  name?: string;
  number?: string;
  onCopy?: (copyValue: string) => void;
  copyIsSuccess?: boolean;
}

export interface TransactionExternalAccountRowTypes {
  title: string;
  name: string;
  value?: string;
  control?: Control;
  enableEdit?: boolean;
  placeholder?: string;
  externalAccounts?: ExternalAccount[];
  disabled?: boolean;
  isLoading?: boolean;
  setValue?: UseFormSetValue<any>;
  tabIndex?: number;
  copyValue?: string;
  copyEnabled?: boolean;
  error?: string;
  selectedExternalAccount?: ExternalAccount;
  setSelelectedExternalAccount: (data: ExternalAccount) => void;
  pspIntegrationAccountId?: string;
  onSearch?: (data: string) => void;
  onClear?: () => void;
  onGetList?: (data: { search?: string; cursor?: string }) => void;
  pagination?: Pagination;
  externalAccountsNextPageIsLoading?: boolean;
  transactionId?: string;
  overdraftEnabled?: boolean;
  isOutgoing?: boolean;
  clearErrors?: UseFormClearErrors<any>;
}

export interface SidepanelReadTypes {
  tabs?: {
    generalPath: string;
    orderPath: string;
    amlPath: string;
    logPath: string;
    changeLogPath?: string;
    warningPath?: string;
    chatTab?: string;
    documentsPath?: string;
    commentsPath?: string;
  };
  parentRef?: RefObject<HTMLDivElement | null>;
  parentScroll?: number;
  warnings?: TransactionWarning[];
  setSubPanelVisible: (data: boolean) => void;
  setSelectedDocumentName: (data: string) => void;
  getTransactionDocument?: (data: string) => void;
  sendMessage?: any;
  deletedAt?: string;
}

export interface TransactionStatusChangeTypes {
  transaction: {
    prevStatus?: BaseellaStatusTransition[];
    nextStatus?: BaseellaStatusTransition[];
  };
  onRollbackStatus?: (status: string) => void;
  onNextStatus?: (status: string) => void;
  updateStatusIsLoading?: boolean;
  isLoadingStatuses?: boolean;
  latestQueuedStatus?: BaseellaStatus;
  baseellaStatus?: BaseellaStatus;
  calcError?: string;
  activeTab: EnumTabs | EnumNostroTabs | EnumExchangeTabs | InternalEnumTabs;
}

export interface TransactionSignaturePopupTypes {
  signaturePopupVisible?: boolean;
  validateCode?: (code: string) => void;
  validateCodeInProgress?: boolean;
  errorMessageSignature?: string;
  onSignatureCancelClick?: () => void;
}

export interface StatusItemTypes {
  isLoadingStatuses?: boolean;
  item: BaseellaStatusTransition;
  onStatusTransition: (status: string) => void;
  updateStatusIsLoading?: boolean;
  disabled?: boolean;
}

export interface IntegrationLog {
  connectionType: string;
  createdAt: string;
  deletedAt?: string;
  eventType?: string;
  id: string;
  pspPartner: PspPartner;
  pspPartnerId: string;
  requestTime?: string;
  responseBody?: string;
  responseTime?: string;
  statusCode: number;
  type: string;
  types: string;
  updatedAt?: string;
  url?: string;
  urlEventType?: string;
  render?: Record<string, React.JSX.Element>;
}
